'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './status-badge';
import { RunDetailsSheet } from './run-details-sheet';
import { ChevronLeft, ChevronRight, Square } from 'lucide-react';
import { stopRun, getHistoryRuns, type RunMeta } from '@/app/actions/runs';
import { toast } from 'sonner';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(start: string, end?: string): string {
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 60) return `${diffSec}s`;
  if (diffMin < 60) return `${diffMin}m ${diffSec % 60}s`;
  return `${diffHour}h ${diffMin % 60}m`;
}

function formatTrigger(trigger: RunMeta['trigger']): string {
  switch (trigger.type) {
    case 'chat':
      return 'Chat';
    case 'subagent':
      return 'Sub-Agent';
    case 'hook':
      return 'Webhook';
    case 'cron':
      return 'Scheduled';
    case 'api':
      return 'API';
    case 'restart':
      return 'Restart';
    default:
      return trigger.type;
  }
}

interface RunsTabProps {
  initialActiveRuns: RunMeta[];
  initialHistoryData?: {
    runs: RunMeta[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export function RunsTab({ initialActiveRuns, initialHistoryData }: RunsTabProps) {
  const [activeRuns, setActiveRuns] = useState<RunMeta[]>(initialActiveRuns);
  const [historyData, setHistoryData] = useState(initialHistoryData || {
    runs: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [stoppingRuns, setStoppingRuns] = useState<Set<string>>(new Set());
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // Force re-render every second to update durations for active runs
  useEffect(() => {
    if (activeRuns.length === 0) return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [activeRuns.length]);

  // SSE connection for real-time updates via Next.js proxy
  useEffect(() => {
    const eventSource = new EventSource('/api/events/runs');

    eventSource.onopen = () => {
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'runs_init':
            setActiveRuns(data.runs);
            break;
          case 'run:created':
            setActiveRuns((prev) => [data.run, ...prev]);
            break;
          case 'run:started':
            setActiveRuns((prev) =>
              prev.map((r) =>
                r.id === data.runId ? { ...r, status: 'running' as const } : r
              )
            );
            break;
          case 'run:status':
            setActiveRuns((prev) =>
              prev.map((r) =>
                r.id === data.runId ? { ...r, status: data.status } : r
              )
            );
            break;
          case 'run:completed':
            // Bug #11: Handle all state updates atomically to avoid race conditions
            {
              const runId = data.runId;
              const completedRun = data.run;

              // Remove from active runs
              setActiveRuns((prev) => prev.filter((r) => r.id !== runId));

              // Add to history if we have valid run data (Bug #22: validate data.run)
              if (completedRun && completedRun.id && completedRun.agent && completedRun.trigger) {
                setHistoryData((prevHistory) => {
                  // Check if already in history (avoid duplicates)
                  if (prevHistory.runs.some((r) => r.id === runId)) {
                    return prevHistory;
                  }

                  // Ensure all required fields are present
                  const runToAdd: RunMeta = {
                    ...completedRun,
                    status: data.status || completedRun.status,
                    usage: data.usage || completedRun.usage,
                    children_ids: completedRun.children_ids || [],
                  };

                  return {
                    ...prevHistory,
                    runs: [runToAdd, ...prevHistory.runs].slice(0, prevHistory.limit),
                    total: prevHistory.total + 1,
                  };
                });
              }

              setStoppingRuns((prev) => {
                const next = new Set(prev);
                next.delete(runId);
                return next;
              });
            }
            break;
        }
      } catch (err) {
        console.error('[RunsTab] Failed to parse SSE event:', err);
      }
    };

    eventSource.onerror = () => {
      setIsConnected(false);
    };

    return () => {
      eventSource.close();
    };
  }, []);

  const loadPage = async (page: number) => {
    setIsLoading(true);
    try {
      const result = await getHistoryRuns({ page, limit: 20 });
      setHistoryData(result);
    } catch (err) {
      console.error('Failed to load runs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = useCallback(async (e: React.MouseEvent, runId: string) => {
    e.stopPropagation();
    setStoppingRuns((prev) => new Set(prev).add(runId));

    const result = await stopRun(runId);
    if (result.success) {
      toast.success('Run stopped');
    } else {
      toast.error(`Failed to stop run: ${result.error}`);
      setStoppingRuns((prev) => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
    }
  }, []);

  const handleRunClick = (runId: string) => {
    setSelectedRunId(runId);
  };

  const handleClose = () => {
    setSelectedRunId(null);
  };

  const { runs: historyRuns, page, totalPages } = historyData;
  const allRuns = [...activeRuns, ...historyRuns];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Agent Runs</h1>
        {isConnected && (
          <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse" title="Connected" />
        )}
        {activeRuns.length > 0 && (
          <span className="text-sm text-muted-foreground">
            ({activeRuns.length} active)
          </span>
        )}
      </div>

      {isLoading && allRuns.length === 0 ? (
        <div className="text-muted-foreground text-center py-8">Loading...</div>
      ) : allRuns.length === 0 ? (
        <div className="text-muted-foreground text-center py-8">No runs</div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allRuns.map((run) => {
                const isActive = activeRuns.some((r) => r.id === run.id);
                return (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer"
                    onClick={() => handleRunClick(run.id)}
                  >
                    <TableCell className="font-medium">{run.agent}</TableCell>
                    <TableCell>
                      <StatusBadge status={run.status} />
                    </TableCell>
                    <TableCell>{formatTrigger(run.trigger)}</TableCell>
                    <TableCell>
                      {formatDuration(run.created_at, isActive ? undefined : run.completed_at)}
                    </TableCell>
                    <TableCell>
                      {run.usage?.totalCost
                        ? `$${run.usage.totalCost.toFixed(4)}`
                        : '-'}
                    </TableCell>
                    <TableCell>{formatDate(run.created_at)}</TableCell>
                    <TableCell>
                      {isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleStop(e, run.id)}
                          disabled={stoppingRuns.has(run.id)}
                          className="h-8 w-8 p-0"
                          title="Stop"
                        >
                          <Square className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadPage(page - 1)}
                disabled={page <= 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadPage(page + 1)}
                disabled={page >= totalPages || isLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      <RunDetailsSheet
        runId={selectedRunId}
        open={!!selectedRunId}
        onClose={handleClose}
      />
    </div>
  );
}
